import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { UserWidgetConfigCreateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { UserCreate } from "../../../user/components/create/UserCreate";
import { UserCreateStore } from "../../../user/store";
import { InputBoolean } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type UserWidgetConfigCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  userSelectedValue?: { displayId: string; id: string } | null;
  onUserSelected?: (value: { displayId: string; id: string } | null) => void;
  onUserChange?: (value: string) => void;
  userOptions?: { displayId: string; id: string }[];
  userError?: string;
  widgetSelectedValue?: { displayId: string; id: string } | null;
  onWidgetSelected?: (value: { displayId: string; id: string } | null) => void;
  onWidgetChange?: (value: string) => void;
  widgetOptions?: { displayId: string; id: string }[];
  widgetError?: string;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabled: boolean;
  enabledError?: string;
  positionSelectedValue?: { displayId: string; id: string } | null;
  onPositionChange?: (value: string) => void;
  position: string;
  positionError?: string;
  configOverrideSelectedValue?: { displayId: string; id: string } | null;
  onConfigOverrideChange?: (value: string) => void;
  configOverride: string;
  configOverrideError?: string;
  displayOrderSelectedValue?: { displayId: string; id: string } | null;
  onDisplayOrderChange?: (value: number | undefined) => void;
  displayOrder: number | undefined;
  displayOrderError?: string;
  userWidgetConfigCreateStore: UserWidgetConfigCreateStore;
};

export const UserWidgetConfigCreateForm: React.FC<UserWidgetConfigCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onUserSelected = () => {},
      userSelectedValue = null,
      userOptions = [],
      userError,
      onUserChange = () => {},
      onWidgetSelected = () => {},
      widgetSelectedValue = null,
      widgetOptions = [],
      widgetError,
      onWidgetChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      position = "{}",
      positionError,
      onPositionChange = () => {},
      configOverride = "{}",
      configOverrideError,
      onConfigOverrideChange = () => {},
      displayOrder = undefined,
      displayOrderError,
      onDisplayOrderChange = () => {},
      userWidgetConfigCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="UserWidgetConfig"
              mode="create"
              values={{}}
              options={{ user: userOptions, widget: widgetOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "user":
                    onUserSelected?.(valor);
                    break;
                  case "widget":
                    onWidgetSelected?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                  case "position":
                    onPositionChange?.(valor);
                    break;
                  case "configOverride":
                    onConfigOverrideChange?.(valor);
                    break;
                  case "displayOrder":
                    onDisplayOrderChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={userWidgetConfigCreateStore.getCardErrors("null")}
              title={langStore.groupUserWidgetConfigLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="user"
                  label={langStore.UserWidgetConfigUserLabel}
                  autoFocus
                  error={Boolean(userError)}
                  helperText={
                    userError ||
                    userWidgetConfigCreateStore.warnings["user"]?.message
                  }
                  selectedValue={userSelectedValue}
                  onChange={(value: string) => onUserChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onUserSelected(value)
                  }
                  options={userOptions}
                  description={langStore.UserWidgetConfigUserDescription}
                  canCreate={rootStore.authStore.hasPermissions("user_create")}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new UserCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <UserCreate
                          userCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewUser"
                      cancelMethod="cancelNewUser"
                    />
                  )}
                />
                <InputReference
                  id="widget"
                  label={langStore.UserWidgetConfigWidgetLabel}
                  error={Boolean(widgetError)}
                  helperText={
                    widgetError ||
                    userWidgetConfigCreateStore.warnings["widget"]?.message
                  }
                  selectedValue={widgetSelectedValue}
                  onChange={(value: string) => onWidgetChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onWidgetSelected(value)
                  }
                  options={widgetOptions}
                  description={langStore.UserWidgetConfigWidgetDescription}
                  canCreate={false}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    userWidgetConfigCreateStore.warnings["enabled"]?.message
                  }
                  label={langStore.UserWidgetConfigEnabledLabel}
                  description={langStore.UserWidgetConfigEnabledDescription}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />

                <InputCodeEditor
                  value={position}
                  error={Boolean(positionError)}
                  helperText={
                    positionError ||
                    userWidgetConfigCreateStore.warnings["position"]?.message
                  }
                  label={langStore.UserWidgetConfigPositionLabel}
                  onChange={(value: string) => onPositionChange(value)}
                  language="json"
                  description={langStore.UserWidgetConfigPositionDescription}
                />

                <InputCodeEditor
                  value={configOverride}
                  error={Boolean(configOverrideError)}
                  helperText={
                    configOverrideError ||
                    userWidgetConfigCreateStore.warnings["configOverride"]
                      ?.message
                  }
                  label={langStore.UserWidgetConfigConfigOverrideLabel}
                  onChange={(value: string) => onConfigOverrideChange(value)}
                  language="json"
                  description={
                    langStore.UserWidgetConfigConfigOverrideDescription
                  }
                />
                <InputNumber
                  value={displayOrder}
                  nullable={true}
                  error={Boolean(displayOrderError)}
                  warning={
                    !displayOrderError &&
                    Boolean(
                      userWidgetConfigCreateStore.warnings["displayOrder"]
                    )
                  }
                  helperText={
                    displayOrderError ||
                    userWidgetConfigCreateStore.warnings["displayOrder"]
                      ?.message
                  }
                  allowNegative={false}
                  label={langStore.UserWidgetConfigDisplayOrderLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.UserWidgetConfigDisplayOrderDescription
                  }
                  onChange={(value: number | undefined) =>
                    onDisplayOrderChange(value)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
